import * as XLSX from 'xlsx'
import { ChildPracticeMetadata, TestCompliance } from "../../../../types"
import { BioCalendarRow, FormattedBioBox, SporeTestingResult, SporeTestReportCustomer, SporeTestResultCustomer } from '../types'
import dayjs, { Dayjs } from 'dayjs'
import { getFailedResultsByPractice } from '../services'
import { AutoclaveType, Frequency } from '../../../practice/biological/types'

const statusMap = new Map([
  [TestCompliance.passedTest, 'Pass'],
  [TestCompliance.missedTest, 'Missed'],
  [TestCompliance.invalidTest, 'Invalid'],
  [TestCompliance.failedTest, 'Failed'],
  [TestCompliance.inactive, 'Inactive'],
])

const exportBioBoxes = async (bioBoxes: FormattedBioBox[]) => {
  const BATCH_SIZE = 500
  let allFormattedItems: any[] = []

  for (let i = 0; i < bioBoxes.length; i += BATCH_SIZE) {
    const batch = bioBoxes.slice(i, i + BATCH_SIZE)
    const formattedItems = batch.map((item) => {
      const expDate = item.exp_date
        ? new Date(item.exp_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        : ''
      return {
        'Status': item.compliance[0].message_short,
        'Box Number': item.display_box_number,
        'Practice': item.name,
        'Location': `${item.city}, ${item.state}`,
        'Tests Left': `${item.tests_left} of ${item.box_size}`,
        'Expiration Date': expDate,
      }
    })
    allFormattedItems = allFormattedItems.concat(formattedItems)
    await new Promise(resolve => setTimeout(resolve, 0))
  }

  const ws = XLSX.utils.json_to_sheet(allFormattedItems)
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, ws, 'Biobox Compliance Report')
  await XLSX.writeFile(wb, 'biobox_compliance_report.xlsx')
}

const getExportData = (customers: SporeTestReportCustomer[], autoclaveTypes: AutoclaveType[], autoclaveFrequencies: Frequency[]) => {
  const autoclaveTypeById = new Map(
    autoclaveTypes.map(type => {
      return [ type.autoclavetype_id, type.autoclavetype_name]
    })
  )

  const autoclaveFrequencyById = new Map(
    autoclaveFrequencies.map(frequency => {
      return [frequency.key_, frequency.frequency_description]
    })
  )

  const sheets = customers.map(customer => {
    
    const street = customer.customer_address?.street ?? ''
    const city = customer.customer_address?.city ?? ''
    const state = customer.customer_address?.state ?? ''
    const zip = customer.customer_address?.zip ?? ''

    const sheetName = `${state.substring(0, 2)}_${customer.name.substring(0, 10)}_${customer.customer_number}`

    const customerData = [
      { A: customer.name },
      { A: customer.doctor_name },
      { A: street },
      { A: `${city}, ${state} ${zip}` },
      { A: `Customer #: ${customer.customer_number}` },
    ]

    const sporetests = customer.sporetesting_results.reduce(
      (tests, test) => {
        if (test.autoclave_id) {
          tests.assigned.push(test)
        } else {
          tests.unassigned.push(test)
        }

        return tests
      },
      { assigned: [] as SporeTestingResult[], unassigned: [] as SporeTestingResult[]}
    )

    const resultsByAutoclaveId = sporetests.assigned.reduce(
      (groups, test) => {
        groups.get(test.autoclave_id)?.push(test)
          ?? groups.set(test.autoclave_id, [test])

        return groups
      },
      new Map() as Map<number, SporeTestingResult[]>
    )

    const unassignedTests = sporetests.unassigned
      .sort((a, b) => new Date(b.test_date).getTime() - new Date(a.test_date).getTime())
      .filter(test => test.test_result !== 'inactive' && (test.sample_date || test.test_date))
      .map(test => {
        return {
          'Test Date': test.sample_date ? dayjs.utc(new Date(test.sample_date)).format('L') : '',
          'Received Date': test.received_date ? dayjs(test.received_date).format('L') : '',
          'Result Date': test.test_date ? dayjs(test.test_date).format('L') : '',
          'Test Number': test.test_number ? test.test_number : '',
          'Operator': test.test_operator ? test.test_operator : 'Unknown',
          'Result': test.test_result,
          'Description': test.result_description,
          'Addressed On': test.addressed_on ? dayjs(test.addressed_on).format('L') : '',
          'Note': test.note ? test.note : ''
        }
      })

    const autoclaves = customer.sporetesting_autoclaves.map(autoclave => {
      const frequencyId = autoclave.autoclave_frequency ?? -1
      const typeId = autoclave.autoclavetype_id ? Number(autoclave.autoclavetype_id) : -1

      const name = autoclave.autoclave_name
      const serial = autoclave.autoclave_serial
      const brand = autoclave.autoclave_brand?.replace(/[^a-zA-Z0-9]/g, '') ?? ''
      const model = autoclave.autoclave_model?.replace(/[^a-zA-Z0-9]/g, '') ?? ''
      const frequency = autoclaveFrequencyById.get(frequencyId) ?? ''
      const type = autoclaveTypeById.get(typeId) ?? ''
      const status = autoclave.active ? 'Yes' : 'No'

      const details = [
        { A: '' },
        { A: `Autoclave Name: ${name}`},
        { A: `Serial/Sterilizer#: ${serial}` },
        { A: `Type:  ${type}` },
        { A: `Brand:  ${brand}` },
        { A: `Model:  ${model}` },
        { A: `Frequency:  ${frequency}` },
        { A: `Status:  ${status}`},
        { A: '' },
      ]

      const autoclaveId = Number(autoclave.autoclave_id)
      const autoclaveTests = resultsByAutoclaveId.get(autoclaveId) ?? []

      const tests = autoclaveTests
        .sort((a, b) => new Date(b.test_date).getTime() - new Date(a.test_date).getTime())
        .map((testResult) => {
          const test = {
            A: testResult.sample_date ? dayjs.utc(new Date(testResult.sample_date)).format('L') : '',
            B: testResult.received_date ? dayjs(testResult.received_date.slice(0, 10)).format('L') : '',
            C: testResult.test_date ? dayjs(testResult.test_date.slice(0, 10)).format('L') : '',
            D: testResult.test_number ? testResult.test_number : '',
            E: testResult.test_operator ? testResult.test_operator : 'Unknown',
            F: testResult.test_result,
            G: testResult.result_description,
            H: testResult.addressed_on ? dayjs(testResult.addressed_on.slice(0, 10)).format('L') : '',
            I: testResult.note ? testResult.note : '',
          }

          return test
        })      

      return { details, tests }
    })

    return {
      sheetName,
      customerData,
      unassignedTests,
      autoclaves,
    }
  })

  return sheets
}

const exportResultsByPractice = (children: SporeTestReportCustomer[], autoclaveTypes: AutoclaveType[], autoclaveFrequencies: Frequency[]) => {
  try {
    if (!children.length) {
      return 'There is no test to download'
    }

    children.sort((a, b) => b.name.localeCompare(a.name))

    const sheets = getExportData(children, autoclaveTypes, autoclaveFrequencies)

    const wb = XLSX.utils.book_new()

    const testHeader = [
      {
        A: 'Test Date',
        B: 'Received Date',
        C: 'Result Date',
        D: 'Test Number',
        E: 'Operator',
        F: 'Result',
        G: 'Description',
        H: 'Addressed On',
        I: 'Note',
      }
    ]    

    sheets.forEach(sheet => {
      const {
        sheetName,
        customerData,
        unassignedTests,
        autoclaves,
      } = sheet

      const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true })

      if (autoclaves.length === 0 && unassignedTests.length === 0) {
        const noTests = [{ '': 'There are no tests for this practice.' }]

        XLSX.utils.sheet_add_json(ws, noTests, { skipHeader: true, origin: -1 })
      } else {
        if (unassignedTests.length > 0) {
          XLSX.utils.sheet_add_json(ws, [{ A: 'Unassigned Test Results' }], { skipHeader: true, origin: "A8" })
          XLSX.utils.sheet_add_json(ws, unassignedTests, { origin: "A10" })
        }

        autoclaves.forEach((autoclave) => {
          XLSX.utils.sheet_add_json(ws, autoclave.details, { skipHeader: true, origin: -1 })
          XLSX.utils.sheet_add_json(ws, testHeader, { skipHeader: true, origin: -1 })

          if (autoclave.tests.length) {
            XLSX.utils.sheet_add_json(ws, autoclave.tests, { skipHeader: true, origin: -1 })
          }
        })
      }

      XLSX.utils.book_append_sheet(wb, ws, sheetName)
    })

    XLSX.writeFile(wb, 'biological_test_results.xlsx')
  } catch (error) {
    console.error(error)
  }
}

const exportLastResults = async (calendarData: BioCalendarRow[] | null, childPractices: ChildPracticeMetadata[] | null) => {
  if (calendarData?.length && childPractices?.length) {
    const wb = XLSX.utils.book_new()
    const data: any = []

    const filteredCustomers = calendarData.map(customer => customer.customerNumber)
    const practices = childPractices
      .filter(customer => filteredCustomers.includes(customer.customer_number))
      .sort((a, b) => b.bio_compliance_status - a.bio_compliance_status || b.bio_message_short.localeCompare(a.bio_message_short))

    const BATCH_SIZE = 500
    for (let i = 0; i < practices.length; i += BATCH_SIZE) {
      const batch = practices.slice(i, i + BATCH_SIZE)
      batch.forEach(practice => {
        const line = {
          Status: practice.bio_message_short,
          'Customer Number': practice.customer_number,
          Name: practice.name,
          City: practice.city,
          State: practice.state.trim(),
          Doctor: practice.doctor_name,
          'Last Result Date': practice.bio_last_test_date ? dayjs(practice.bio_last_test_date).format('L') : 'N/A',
          'Last Test Status': practice.bio_last_status ? practice.bio_last_status : 'N/A',
          'Active Autoclave': practice.bio_num_active_autoclaves,
          'Ontraq Account': practice.ot_account ? 'Yes' : 'No',
        }
        data.push(line)
      })
      await new Promise(resolve => setTimeout(resolve, 0))
    }

    const ws = XLSX.utils.json_to_sheet(data)

    XLSX.utils.book_append_sheet(wb, ws, 'Summary')
    await XLSX.writeFile(wb, 'test_test_results.xlsx')
  }
}

const exportAllResults = async (calendarData: BioCalendarRow[] | null) => {
  if (calendarData?.length) {
    const BATCH_SIZE = 500
    let allData: any[] = []

    for (let i = 0; i < calendarData.length; i += BATCH_SIZE) {
      const batch = calendarData.slice(i, i + BATCH_SIZE)
      const data = batch.map((practice) => {
        const line: any = {
          'Customer #': practice.customerNumber,
          Name: practice.practiceName,
          City: practice.practiceCity,
          State: practice.practiceState,
          Doctor: practice.doctor_name,
          'Completion %': practice.compliance.totalStatus,
        }

        Object.keys(practice.testResultsByWeek).forEach((dateKey) => {
          const result = practice.testResultsByWeek[dateKey]
          line[result.dateRange] = statusMap.get(result.message as TestCompliance) ?? ''
        })
        return line
      })
      allData = allData.concat(data)

      await new Promise(resolve => setTimeout(resolve, 0))
    }

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(allData)
    XLSX.utils.book_append_sheet(wb, ws, 'Last Year Biological Summary')
    await XLSX.writeFile(wb, 'all_results_summary.xlsx')
  }
}

const exportFailedTestResults = async (
  calendarData: BioCalendarRow[] | null,
  customerNumber: string,
  groupIds?: number[],
  fromDate?: Dayjs,
  toDate?: Dayjs
) => {
  const customerNumbers = new Set(
    calendarData?.map(customer => customer.customerNumber) ?? []    
  )

  if (!customerNumbers.size) {
    return 'There are no practices selected'
  }  

  try {
    const groups = groupIds?.length ? groupIds : null
    const dateRange = fromDate && toDate ? [fromDate, toDate] : null

    const practices = await getFailedResultsByPractice(customerNumber, groups, dateRange)

    const customers = practices.filter((customer: SporeTestResultCustomer) => {
      return customerNumbers.has(customer.customer_number)
    })

    if (customers.length === 0) {
      return 'There are no matching practices'
    }

    customers.forEach(customer => {
      customer
        .sporetesting_results
        .sort((a, b) => {
          const first = new Date(a.test_date).getTime()
          const second = new Date(b.test_date).getTime()

          return second - first
        })
    })

    const testResults = customers.flatMap(customer => {
      return customer.sporetesting_results
    })

    if (testResults.length === 0) {
      return 'There are no practices with failed spore tests'
    }

    const customerByNumber = new Map<string, SporeTestResultCustomer>(
      customers.map(customer => {
        return [customer.customer_number, customer]
      })
    )

    const autoclaves = customers.flatMap(customer => {
      return customer.sporetesting_autoclaves
    })

    const autoclaveNameById = new Map<string, string>(
      autoclaves.map(autoclave => {
        return [autoclave.autoclave_id, autoclave.autoclave_name]
      })
    )

    const rows = testResults.map(result => {
      const { customer_id, autoclave_id, test_date, addressed_on, note } = result
      
      const autoclaveId = String(autoclave_id)
      const autoclaveName = autoclaveNameById.get(autoclaveId) ?? 'Unassigned'

      const customer = customerByNumber.get(customer_id)
      const customerNumber = customer?.customer_number ?? ''
      const customerName = customer?.name ?? ''
      const city = customer?.city ?? ''
      const state = customer?.state ?? ''
      const doctor = customer?.doctor_name ?? ''

      const resultDate = test_date ? dayjs(test_date).format('L') : ''
      const actionDate = addressed_on ? dayjs(addressed_on).format('L') : ''
      const action = note ? note : ''

      const row = {
        'Customer #': customerNumber,
        'Practice Name': customerName,
        City: city,
        State: state,
        Doctor: doctor,
        'Autoclave Name': autoclaveName,
        'Test Result Date': resultDate,
        'Corrective Action Date': actionDate,
        'Corrective Action': action,
      }

      return row
    })

    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(rows)

    XLSX.utils.book_append_sheet(wb, ws, 'Failed Test Report')
    XLSX.writeFile(wb, 'corrective_action.xlsx')
  } catch (error) {
    console.error('Error fetching spore test results:', error)
  }
}

export {
  exportAllResults,
  exportFailedTestResults,
  exportResultsByPractice,
  exportLastResults,
  exportBioBoxes,
}